/**
 * Guardrails view helpers — extracted so they can be unit-tested in
 * isolation from the React render path.
 *
 *   - rulesFingerprint(rules)        — stable hash for draft vs active diff
 *   - draftStateFor(rule, active)    — classify a single rule's draft state
 *   - computePolicyHealth(rules, cats) — composite health number used by
 *                                        the PolicyHealthBar
 *   - nextVersionLabel(prev)         — bumps "v1" → "v2", else appends a
 *                                       timestamp
 *   - formatRelativeShort(iso)       — short "Xs ago" / "Ym ago" formatter
 */

import type { Rule, RuleAction, RuleCategory, RuleType } from '@sill/types';

/**
 * The sensible default `action_on_match` for a freshly-added rule of a given
 * type. r07 is a human-in-the-loop gate — its purpose is to ROUTE a
 * destructive action over the ceiling to a human, so it defaults to `escalate`
 * (the 202/pending flow), not `reject` (block outright). Every other rule
 * defaults to `reject`. The merchant can still override; editing an existing
 * rule always preserves its saved action.
 */
export function recommendedAction(type: RuleType | null | undefined): RuleAction {
  if (type === 'r07') return 'escalate';
  // ADR-0260 §8 founder decision: r32 defaults to `escalate` (hold for
  // review) — a legitimate busy agent is held, not refused; a key thief's
  // orders wait for a human. Merchants who want a hard stop opt in to
  // `reject`. The publish validator REJECTS `allow` on an r32 rule.
  if (type === 'r32') return 'escalate';
  // r18's manifest_hashes list is an allow-list: matching a known-good hash
  // should pass the agent through, not reject it.
  if (type === 'r18') return 'allow';
  return 'reject';
}

export function rulesFingerprint(rules: readonly Rule[]): string {
  return JSON.stringify(
    rules.map((r) => ({
      rule_id: r.rule_id,
      type: r.type,
      params: r.params,
      action_on_match: r.action_on_match,
      enabled: r.enabled,
      order: r.order,
    })),
  );
}

export type DraftState = 'new' | 'edited' | 'unchanged';

export function draftStateFor(
  rule: Rule,
  active_map: ReadonlyMap<string, Rule>,
): DraftState {
  const a = active_map.get(rule.rule_id);
  if (a === undefined) return 'new';
  if (rulesFingerprint([rule]) !== rulesFingerprint([a])) return 'edited';
  return 'unchanged';
}

export interface PolicyHealth {
  healthPct: number;
  enabledCount: number;
  coveredCats: number;
  totalCats: number;
}

/**
 * Composite policy health number.
 *   70% from category coverage (covered / total)
 * + 30% from rule activation (enabled / total)
 *
 * Per the 2026-06-01 security review: coverage breadth is the security-
 * relevant signal — a policy covering every category beats a policy with
 * many redundant rules in two categories. Activation stays in the mix as
 * a tie-breaker for hygiene (a category whose rules are mostly disabled
 * shouldn't read fully covered).
 *
 * `cats` is the list of categories that "count" toward coverage.
 * `'custom'` is intentionally excluded by callers because the DSL escape
 * valve isn't a category a healthy policy "must cover".
 */
export function computePolicyHealth(
  rules: readonly Rule[],
  cats: readonly RuleCategory[],
): PolicyHealth {
  const enabled = rules.filter((r) => r.enabled).length;
  const ruleActivationPct = rules.length > 0 ? (enabled / rules.length) * 100 : 0;
  const covered = cats.filter((c) =>
    rules.some((r) => r.category === c && r.enabled),
  ).length;
  const categoryCoveragePct = cats.length > 0
    ? (covered / cats.length) * 100
    : 0;
  const composite = Math.round(
    categoryCoveragePct * 0.7 + ruleActivationPct * 0.3,
  );
  return {
    healthPct: composite,
    enabledCount: enabled,
    coveredCats: covered,
    totalCats: cats.length,
  };
}

const VERSION_RE = /^[A-Za-z0-9_.-]+$/;
const MAX_VERSION_LEN = 64;

export function nextVersionLabel(prev?: string): string {
  if (prev === undefined || prev.length === 0) return 'v1';
  // Bump the LAST `v<number>` occurrence IN PLACE — preserving any prefix
  // (e.g. `s001-v1` → `s001-v2`) and NEVER appending. The previous version
  // appended a `-<timestamp>` suffix whenever `prev` wasn't a bare `vN`, so a
  // non-`vN` version (a legacy timestamped label, a `v1.0`) grew by ~16 chars
  // on every publish until it blew past the server's 64-char version cap and
  // the publish 400'd with `version_malformed`. Bumping in place keeps the
  // label format-valid (`/^[A-Za-z0-9_.-]+$/`) and length-stable forever.
  const lastVersionNumber = /v(\d+)(?![\s\S]*v\d)/;
  const m = prev.match(lastVersionNumber);
  let next: string;
  if (m && m[1] !== undefined) {
    const n = Number.parseInt(m[1], 10);
    next = Number.isInteger(n) ? prev.replace(lastVersionNumber, `v${n + 1}`) : `${prev}-v1`;
  } else {
    next = `${prev}-v1`;
  }
  // Guard: if prev contained characters outside the server's allowed set
  // (e.g. ':' from ISO 8601 timestamps accumulated by the old appending code),
  // or the result exceeds the 64-char cap, fall back to a clean numeric label.
  if (!VERSION_RE.test(next) || next.length > MAX_VERSION_LEN) {
    const vNum = next.match(/v(\d+)/);
    return vNum ? `v${vNum[1]}` : 'v1';
  }
  return next;
}

/**
 * One-line human summary of a rule's params for the row line. Replaces
 * the previous `params {JSON.stringify(rule.params)}` raw dump that
 * blew up the row height on long allowlists (UX review 2026-06-01).
 *
 * The summary intentionally drops the rule_id / type prefix — those
 * still render alongside it, this only describes the *params*.
 */
export function summarizeRuleParams(rule: Rule): string {
  switch (rule.params.type) {
    case 'r01': {
      const list = (rule.params as { allowlisted_agent_ids: string[] }).allowlisted_agent_ids ?? [];
      const n = list.length;
      return `${n} agent${n === 1 ? '' : 's'} allowed`;
    }
    case 'r05': {
      const p = rule.params as {
        max_amount_usd?: number;
        caps?: Record<string, number>;
        on_unlisted_currency?: string;
      };
      const entries: Array<[string, number]> = [];
      if (p.caps !== undefined) {
        for (const [code, amount] of Object.entries(p.caps)) {
          if (typeof amount === 'number') entries.push([code, amount]);
        }
      }
      if (typeof p.max_amount_usd === 'number' && !entries.some(([c]) => c === 'USD')) {
        entries.push(['USD', p.max_amount_usd]);
      }
      const caps = entries.map(([code, amount]) => `${code} ≤ ${amount}`).join(', ');
      const tail = p.on_unlisted_currency === 'reject' ? ' · others rejected' : '';
      return caps.length > 0 ? `${caps}${tail}` : 'no cap';
    }
    case 'r06': {
      const usd = (rule.params as { daily_cap_usd: number }).daily_cap_usd;
      return `$${usd}/day per user`;
    }
    case 'r31': {
      // ADR-0151 — cross-agent organization budget cap. USD + rolling window.
      // Default window 86400s (24h). Render in the most readable unit (hours
      // or days) so a 24h or 7d window doesn't show as "86400s".
      const p = rule.params as {
        org_budget_usd?: number;
        window_seconds?: number;
      };
      const usd = typeof p.org_budget_usd === 'number' ? p.org_budget_usd : 0;
      const win = typeof p.window_seconds === 'number' ? p.window_seconds : 86_400;
      const windowLabel =
        win % 86_400 === 0
          ? `${win / 86_400}d`
          : win % 3_600 === 0
            ? `${win / 3_600}h`
            : `${win}s`;
      return `$${usd}/${windowLabel} per organization (all agents)`;
    }
    case 'r07': {
      const p = rule.params as {
        max_auto_approve_usd?: number;
        auto_approve_caps?: Record<string, number>;
      };
      const entries: Array<[string, number]> = [];
      if (p.auto_approve_caps !== undefined) {
        for (const [code, amount] of Object.entries(p.auto_approve_caps)) {
          if (typeof amount === 'number') entries.push([code, amount]);
        }
      }
      if (typeof p.max_auto_approve_usd === 'number' && !entries.some(([c]) => c === 'USD')) {
        entries.push(['USD', p.max_auto_approve_usd]);
      }
      const caps = entries.map(([code, amount]) => `${code} ≤ ${amount}`).join(', ');
      // An unlisted currency always routes to a human (no auto-approve).
      return caps.length > 0
        ? `Human review on destructive · auto-approve ${caps} · others reviewed`
        : 'Human review on destructive actions';
    }
    case 'r12': {
      const p = rule.params as { mode?: string; countries?: string[] };
      const n = (p.countries ?? []).length;
      return `${p.mode ?? 'deny'} ${n} countr${n === 1 ? 'y' : 'ies'}`;
    }
    case 'r18': {
      const n = ((rule.params as { manifest_hashes?: string[] }).manifest_hashes ?? []).length;
      return `${n} approved digest${n === 1 ? '' : 's'}`;
    }
    case 'r23': {
      const secs = (rule.params as { max_validity_seconds: number }).max_validity_seconds;
      return `≤ ${secs}s validity window`;
    }
    case 'r25': {
      const bytes = (rule.params as { max_body_bytes: number }).max_body_bytes;
      return `≤ ${bytes.toLocaleString()} B body`;
    }
    case 'r28':
      return 'kill switch — rejects every mandate';
    case 'r10':
    case 'r20':
    case 'r22':
    case 'r03':
    case 'r04':
    case 'r13':
      return 'no params';
    case 'r_custom': {
      const expr = (rule.params as { expression: string }).expression;
      const trimmed = expr.length > 60 ? `${expr.slice(0, 57)}…` : expr;
      return `expr: ${trimmed}`;
    }
    default:
      // Every params-less rule (r02/r08/r09/r11/r14/r15/r17/r19/r21/r24/r26/r27)
      // lands here — show "no params", NOT the bare type (which produced the
      // confusing "r09 · r09" on the rule cards).
      return 'no params';
  }
}

export function humanizeJsonError(err: unknown, rawText: string): string {
  if (/,\s*[}\]]/.test(rawText)) {
    return 'Invalid JSON — remove the trailing comma before `}` or `]`.';
  }
  if (/'[^']*'\s*:/.test(rawText)) {
    return 'Invalid JSON — keys must use double quotes, not single quotes.';
  }
  if (err instanceof Error) {
    const msg = err.message;
    const longCtx = msg.match(/\.\.\.([\s\S]{1,120}?)\.\.\./)?.[1];
    const shortCtx = msg.match(/,\s*"([\s\S]+?)"\s+is not valid JSON$/)?.[1];
    const ctx = longCtx ?? shortCtx;
    if (ctx !== undefined) {
      const unquoted = ctx.replace(/"(?:[^"\\]|\\.)*"/g, '""');
      for (const [, word] of unquoted.matchAll(/\b([a-z]{2,8})\b/g)) {
        if (word === undefined || word === 'true' || word === 'false' || word === 'null') continue;
        if (/^tr/.test(word)) return `\`${word}\` is not valid — did you mean \`true\`?`;
        if (/^f[al]/.test(word)) return `\`${word}\` is not valid — did you mean \`false\`?`;
        if (/^nu/.test(word)) return `\`${word}\` is not valid — did you mean \`null\`?`;
      }
      if (longCtx !== undefined) return `Invalid JSON near: …${ctx.trim()}…`;
    }
    const posMatch = msg.match(/at line (\d+) column (\d+)/);
    if (posMatch) {
      return `Invalid JSON at line ${posMatch[1]}, column ${posMatch[2]}.`;
    }
  }
  return 'Invalid JSON — check for typos, missing quotes, commas, or brackets.';
}

export function formatRelativeShort(iso: string, now_ms: number = Date.now()): string {
  const ms = now_ms - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  if (ms < 5_000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3_600_000)}h ago`;
}

/**
 * Plain-English explanation of an evaluator `reason` code (the developer
 * strings the rule handlers emit, e.g. `destructive_over_threshold`). Used to
 * render the QuickTest verdict and any reason surfaced to a merchant — the raw
 * snake_case code is not friendly. Falls back to a Title-Cased version of the
 * code so a newly-added reason is still readable before it gets an entry here.
 */
const REASON_FRIENDLY: Record<string, string> = {
  // spend (r05 / r14 / r15)
  amount_exceeds_cap: 'Amount is over the per-transaction cap for this currency.',
  currency_uncapped: 'This currency has no configured cap, and unlisted currencies are blocked.',
  cart_exceeds_intent_ceiling: 'Cart total exceeds the budget the user signed in the intent.',
  cart_currency_mismatch: 'Cart currency does not match the signed intent currency.',
  cart_total_differs_from_quoted: 'Cart total does not match the quoted total.',
  // scope / HITL (r07)
  destructive_over_threshold:
    'This refund or cancellation is over the auto-approve ceiling for its currency.',
  destructive_non_usd_unevaluable:
    'Refund or cancellation in a currency with no auto-approve ceiling — sent for human review.',
  destructive_currency_uncapped:
    'Refund or cancellation in a currency with no auto-approve ceiling — sent for human review.',
  // identity / allowlist (r01)
  agent_not_allowlisted: 'This agent is not on the allowlist.',
  // geo (r12)
  geo_denied: 'This country is on the blocklist.',
  geo_not_allowlisted: 'This country is not on the allowlist.',
  geo_country_unavailable: 'The request country could not be determined.',
  // validity (r23)
  validity_window_too_long: 'The mandate is valid for longer than this rule allows.',
  // body size (r25)
  payload_too_large: 'The mandate body is larger than the configured limit.',
  // manifest integrity (r18)
  skill_manifest_pinned: 'Hash matched an approved entry.',
  skill_manifest_mismatch: 'The skill manifest does not match an approved version.',
  skill_manifest_absent: 'No skill manifest was provided.',
  // consent / scoping (r17 / r19)
  subscription_without_user_consent: 'A subscription was created without explicit user consent.',
  cross_customer_object_access: 'This request tried to access another customer’s data.',
  scoped_access_without_user: 'A customer-scoped action arrived without an identified user.',
  // kill switch (r28)
  emergency_kill_switch_engaged: 'The kill switch is engaged — every mandate is rejected.',
  // custom (r_custom)
  predicate_failed: 'The custom rule expression matched this mandate.',
  // misconfig / engine (any rule)
  rule_params_invalid: 'This rule is misconfigured — open it and check its settings.',
  mandate_amount_invalid: 'The mandate amount is malformed.',
  rule_budget_exhausted: 'Evaluation ran out of time before this rule finished.',
};

function titleCaseFromSnake(s: string): string {
  return s
    .split('_')
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function friendlyReason(reason: string | undefined): string | undefined {
  if (reason === undefined || reason === '') return undefined;
  return REASON_FRIENDLY[reason] ?? titleCaseFromSnake(reason);
}

/**
 * Turn a raw QuickTest run error (e.g. `bad_request:400 —
 * rule_0_auto_approve_caps_currency_not_iso4217`) into a merchant-readable
 * sentence. The publish/test validator returns `rule_<i>_<reason>` codes; the
 * most common after the ISO-4217 tightening is an already-published rule whose
 * currency key is not a real code (a typo like `EUO`). Falls back to the raw
 * message so nothing is ever swallowed silently.
 */
export function friendlyTestError(raw: string): string {
  const ruleMatch = /rule_(\d+)_/.exec(raw);
  const ruleLabel = ruleMatch !== null ? `Rule #${Number(ruleMatch[1]) + 1}` : 'One of your rules';
  if (/caps_currency_not_iso4217/.test(raw)) {
    return `${ruleLabel} uses a currency code that isn’t a real ISO-4217 currency (for example a typo like “EUO” for “EUR”). Open that rule and fix the currency, then re-run the test.`;
  }
  if (/_no_caps_configured/.test(raw)) {
    return `${ruleLabel} has no currencies configured. Open it and add at least one currency and amount.`;
  }
  if (/caps_amount_not_non_negative_number/.test(raw)) {
    return `${ruleLabel} has a currency amount that isn’t a valid non-negative number. Open it and fix the amount.`;
  }
  return raw;
}
